// components/markdown/Artifact.tsx
import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink } from 'lucide-react';
import { useArtifacts } from '@/contexts/ArtifactContext';
import type { BaseCustomProps } from './types';

interface ArtifactProps extends BaseCustomProps {
  id?: string;
  title?: string;
  type?: string;
}

/**
 * Interactive artifact component that renders a clickable badge
 * Users can click to open the artifact in the side panel
 */
export const Artifact: React.FC<ArtifactProps> = ({ id, title, type, children, ...props }) => {
  const { setActiveArtifactId, setPanelOpen, addArtifact, artifacts } = useArtifacts();

  // Extract content from children - handle both string and JSX content
  const extractContent = useCallback((children: React.ReactNode): string => {
    if (typeof children === 'string') {
      return children;
    }
    
    if (React.isValidElement(children)) {
      // Handle code blocks and other content
      if (children.type === 'pre' || children.type === 'code') {
        return React.Children.toArray(children.props.children).join('');
      }
      
      // For other elements, try to extract text content
      if (typeof children.props?.children === 'string') {
        return children.props.children;
      }
      
      // Recursively extract from nested children
      return React.Children.toArray(children.props?.children || [])
        .map(child => extractContent(child))
        .join('');
    }
    
    if (Array.isArray(children)) {
      return children.map(child => extractContent(child)).join('');
    }
    
    return String(children || '');
  }, []);

  // Ensure the artifact exists in the context when the component renders
  React.useEffect(() => {
    if (id && title && type) {
      const content = extractContent(children);
      const artifactData = {
        id,
        title,
        type,
        content: content.trim()
      };
      
      // Always add/update the artifact - let the context handle deduplication
      addArtifact(artifactData);
    }
  }, [id, title, type, children, addArtifact, extractContent]);

  const handleClick = () => {
    if (id) {
      setActiveArtifactId(id);
      setPanelOpen(true);
    }
  };

  // Don't render if missing required props
  if (!id || !title) {
    return null;
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="inline-flex items-center gap-2 mx-1 h-auto py-1 px-2 text-xs font-normal"
      onClick={handleClick}
      title={`Open "${title}" in artifact panel`}
    >
      <FileText className="h-3 w-3" />
      <span className="truncate max-w-[120px]">{title}</span>
      {type && (
        <Badge variant="secondary" className="text-xs px-1 py-0">
          {type}
        </Badge>
      )}
      <ExternalLink className="h-3 w-3 opacity-60" />
    </Button>
  );
};

export default Artifact;